// src/routes/UserManagement.js
import React, { useState, useEffect } from 'react';
import axios from '../../utils/axiosConfig';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Card,
    Typography,
    Box,
    Select,
    MenuItem,
    Button,
    FormControl,
    TextField,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Tooltip,
    InputAdornment,
    Alert,
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import BuildIcon from '@mui/icons-material/Build';
import KeyIcon from '@mui/icons-material/Key';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

export default function UserManagement() {
    // ---------------------
    // バックエンドのURL（.env などから取得）
    // API ベース URL。
    // .env に REACT_APP_API_URL="https://example.com/api" のように `/api` まで入れておくこと。
    // 未設定なら同一オリジン + `/api` を補完し、末尾スラッシュは削除。
    const BACKEND_URL = process.env.REACT_APP_API_URL;

    // ---------------------
    // アコーディオンの開閉管理
    // ---------------------
    const [expanded, setExpanded] = useState('panel1');
    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    // ---------------------
    // ユーザー管理 State
    // ---------------------
    const [userList, setUserList] = useState([]);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserPassword, setNewUserPassword] = useState('');
    const [newUserRole, setNewUserRole] = useState('general');
    const [currentUserId, setCurrentUserId] = useState(null);
    
    // 新規追加: モーダル制御用
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [error, setError] = useState(null);

    // OpenAI API Key管理用のstate
    const [openApiKeyModal, setOpenApiKeyModal] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [newApiKey, setNewApiKey] = useState('');
    const [apiKeyError, setApiKeyError] = useState(null);

    // パスワード変更用のstate
    const [openPasswordModal, setOpenPasswordModal] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState(null);

    // メールアドレス変更用のstate
    const [openEmailModal, setOpenEmailModal] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [emailError, setEmailError] = useState(null);

    // 検索フィルター関数
    const filteredUsers = userList.filter(user => {
        const searchLower = searchQuery.toLowerCase();
        const companyProfile = user.company_profile || {};
        
        return (
            user.email.toLowerCase().includes(searchLower) ||
            user.role.toLowerCase().includes(searchLower) ||
            (companyProfile.company_name || '').toLowerCase().includes(searchLower) ||
            (companyProfile.industry || '').toLowerCase().includes(searchLower) ||
            (companyProfile.location || '').toLowerCase().includes(searchLower)
        );
    });

    // モーダルを開く
    const handleOpenCreateModal = () => {
        setNewUserEmail('');
        setNewUserPassword('');
        setNewUserRole('general');
        setOpenCreateModal(true);
    };

    // モーダルを閉じる
    const handleCloseCreateModal = () => {
        setOpenCreateModal(false);
        setError(null);
    };

    // ロールに応じたアイコンを返す
    const getRoleIcon = (role) => {
        switch (role) {
            case 'admin':
                return <AdminPanelSettingsIcon />;
            case 'custom':
                return <BuildIcon />;
            case 'interview_only':
                return <PersonIcon />;
            default:
                return <PersonIcon />;
        }
    };

    // ロールに応じた表示名を返す
    const getRoleName = (role) => {
        switch (role) {
            case 'admin':
                return '管理者';
            case 'custom':
                return 'カスタム';
            case 'interview_only':
                return '子アカウント';
            default:
                return '一般ユーザー';
        }
    };

    // ---------------------
    // ユーザー一覧を取得
    // ---------------------
    const fetchUsers = async () => {
        try {
            const res = await axios.get(`${BACKEND_URL}/users/`);
            // res.data: [ { id, email, role, locked, ...}, ... ]
            setUserList(res.data);
        } catch (err) {
            console.error('ユーザー取得エラー:', err);
            alert('ユーザー取得に失敗しました。');
        }
    };
    // ...
    useEffect(() => {
        fetchCurrentUser();
        fetchUsers();
    }, []);
    const fetchCurrentUser = async () => {
        try {
            const res = await axios.get(`${BACKEND_URL}/auth/me`);
            // { id, email, role, locked } などが返る想定
            setCurrentUserId(res.data.id);
        } catch (err) {
            console.error('現在のユーザ取得失敗:', err);
            // 必要に応じてエラー処理
        }
    };
    // ---------------------
    // マウント時にユーザー一覧を取得
    // ---------------------
    useEffect(() => {
        fetchUsers();
        // eslint-disable-next-line
    }, []);

    // ---------------------
    // ユーザー追加
    // ---------------------
    const handleCreateUser = async () => {
        setError(null);
        
        // バリデーション
        if (!newUserEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            setError('正しいメールアドレスを入力してください。');
            return;
        }
        if (newUserPassword.length < 6) {
            setError('パスワードは6文字以上にしてください。');
            return;
        }

        try {
            await axios.post(
                `${BACKEND_URL}/users/`,
                {
                    email: newUserEmail,
                    password: newUserPassword,
                    role: newUserRole,
                }
            );
            handleCloseCreateModal();
            fetchUsers();
        } catch (err) {
            console.error('ユーザー追加失敗:', err);
            setError(err.response?.data?.detail || 'ユーザー追加に失敗しました。');
        }
    };

    // ---------------------
    // ユーザー削除
    // ---------------------
    const handleDeleteUser = async (userId) => {
        // 対象ユーザーを取得
        const targetUser = userList.find(user => user.id === userId);
        
        // 管理者は削除できない
        if (targetUser.role === 'admin') {
            alert('管理者ユーザーは削除できません。');
            return;
        }

        if (!window.confirm('本当に削除しますか？')) return;
        try {
            await axios.delete(`${BACKEND_URL}/users/${userId}`);
            alert('ユーザーを削除しました。');
            fetchUsers();
        } catch (err) {
            console.error('ユーザー削除失敗:', err);
            alert('削除失敗');
        }
    };

    // ---------------------
    // ユーザー権限変更
    // ---------------------
    const handleChangeUserRole = async (userId, newRole) => {
        try {
            await axios.patch(
                `${BACKEND_URL}/users/${userId}/role`,
                { new_role: newRole }
            );
            alert('権限を変更しました。');
            fetchUsers();
        } catch (err) {
            console.error('権限変更失敗:', err);
            alert('権限変更失敗');
        }
    };

    // ---------------------
    // ユーザーロック切り替え
    // ---------------------
    const handleToggleLock = async (userId, locked) => {
        // 対象ユーザーを取得
        const targetUser = userList.find(user => user.id === userId);
        
        // 管理者はロックできない
        if (targetUser.role === 'admin') {
            alert('管理者ユーザーはロックできません。');
            return;
        }

        try {
            await axios.patch(`${BACKEND_URL}/users/${userId}/lock`, null);
            alert(locked ? 'ロック解除しました。' : 'ロックしました。');
            fetchUsers();
        } catch (err) {
            console.error('ロック変更失敗:', err);
            alert('ロック変更失敗');
        }
    };

    // APIキーをマスクする関数
    const maskApiKey = (apiKey) => {
        if (!apiKey) return '';
        // 最初の3文字と最後の4文字を表示し、残りを*でマスク
        return `${apiKey.slice(0, 3)}${'*'.repeat(apiKey.length - 7)}${apiKey.slice(-4)}`;
    };

    // APIキー更新モーダルを開く
    const handleOpenApiKeyModal = (userId) => {
        const user = userList.find(u => u.id === userId);
        setSelectedUserId(userId);
        setNewApiKey('');
        setApiKeyError(null);
        setOpenApiKeyModal(true);
    };

    // APIキー更新モーダルを閉じる
    const handleCloseApiKeyModal = () => {
        setOpenApiKeyModal(false);
        setSelectedUserId(null);
        setNewApiKey('');
        setApiKeyError(null);
    };

    // APIキーを更新
    const handleUpdateApiKey = async () => {
        try {
            await axios.patch(
                `${BACKEND_URL}/users/${selectedUserId}/openai-key`,
                { openai_api_key: newApiKey }
            );
            handleCloseApiKeyModal();
            fetchUsers();
        } catch (err) {
            console.error('APIキー更新失敗:', err);
            setApiKeyError(err.response?.data?.detail || 'APIキーの更新に失敗しました。');
        }
    };

    // APIキーを削除
    const handleDeleteApiKey = async (userId) => {
        if (!window.confirm('本当にAPIキーを削除しますか？')) return;
        try {
            await axios.delete(`${BACKEND_URL}/users/${userId}/openai-key`);
            fetchUsers();
        } catch (err) {
            console.error('APIキー削除失敗:', err);
            alert('APIキーの削除に失敗しました。');
        }
    };

    // パスワード変更モーダルを開く
    const handleOpenPasswordModal = (userId) => {
        setSelectedUserId(userId);
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError(null);
        setOpenPasswordModal(true);
    };

    // パスワード変更モーダルを閉じる
    const handleClosePasswordModal = () => {
        setOpenPasswordModal(false);
        setSelectedUserId(null);
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError(null);
    };

    // パスワードを更新
    const handleUpdatePassword = async () => {
        // バリデーション
        if (newPassword.length < 6) {
            setPasswordError('パスワードは6文字以上で入力してください。');
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError('パスワードが一致しません。');
            return;
        }

        try {
            await axios.patch(
                `${BACKEND_URL}/users/${selectedUserId}/password`,
                { new_password: newPassword }
            );
            handleClosePasswordModal();
            alert('パスワードを更新しました。');
        } catch (err) {
            console.error('パスワード更新失敗:', err);
            setPasswordError(err.response?.data?.detail || 'パスワードの更新に失敗しました。');
        }
    };

    // メールアドレス変更モーダルを開く
    const handleOpenEmailModal = (userId) => {
        const user = userList.find(u => u.id === userId);
        setSelectedUserId(userId);
        setNewEmail(user.email); // 現在のメールアドレスを初期値として設定
        setEmailError(null);
        setOpenEmailModal(true);
    };

    // メールアドレス変更モーダルを閉じる
    const handleCloseEmailModal = () => {
        setOpenEmailModal(false);
        setSelectedUserId(null);
        setNewEmail('');
        setEmailError(null);
    };

    // メールアドレスを更新
    const handleUpdateEmail = async () => {
        // 基本的なバリデーション
        if (!newEmail || !newEmail.includes('@')) {
            setEmailError('有効なメールアドレスを入力してください。');
            return;
        }

        try {
            await axios.patch(
                `${BACKEND_URL}/users/${selectedUserId}/email`,
                { new_email: newEmail }
            );
            handleCloseEmailModal();
            alert('メールアドレスを更新しました。');
            fetchUsers(); // ユーザー一覧を更新
        } catch (err) {
            console.error('メールアドレス更新失敗:', err);
            setEmailError(err.response?.data?.detail || 'メールアドレスの更新に失敗しました。');
        }
    };

    return (
        <Accordion
            expanded={expanded === 'panel1'}
            onChange={handleAccordionChange('panel1')}
            defaultExpanded
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    ユーザ管理・権限管理
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                {/* 検索バーとユーザー追加ボタン */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="ユーザーを検索..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ flexGrow: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreateModal}
                    >
                        新規ユーザー
                    </Button>
                </Box>

                {/* ユーザー一覧テーブル */}
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>会社情報</TableCell>
                                <TableCell>ユーザー</TableCell>
                                <TableCell>ロール</TableCell>
                                <TableCell align="center">ステータス</TableCell>
                                <TableCell align="center">OpenAI API Key</TableCell>
                                <TableCell align="right">アクション</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredUsers.map((user) => {
                                const isMe = (user.id === currentUserId);
                                const companyProfile = user.company_profile || {};
                                const isAdmin = user.role === 'admin';
                                const isChildAccount = user.role === 'interview_only';
                                const hasApiKey = user.openai_api_key && user.openai_api_key.trim() !== '';
                                return (
                                    <TableRow key={user.id}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {companyProfile.company_name || '未設定'}
                                                </Typography>
                                                {companyProfile.company_name && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {[
                                                            companyProfile.industry,
                                                            companyProfile.scale,
                                                            companyProfile.location
                                                        ].filter(Boolean).join(' | ')}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {user.email}
                                                {isMe && (
                                                    <Chip
                                                        label="You"
                                                        size="small"
                                                        color="success"
                                                        sx={{ height: 20, color: '#ffffff' }}
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {getRoleIcon(user.role)}
                                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                                    <Select
                                                        value={user.role}
                                                        onChange={(e) => handleChangeUserRole(user.id, e.target.value)}
                                                        size="small"
                                                        disabled={isChildAccount}
                                                    >
                                                        <MenuItem value="general">一般ユーザー</MenuItem>
                                                        <MenuItem value="admin">管理者</MenuItem>
                                                        <MenuItem value="custom">カスタム</MenuItem>
                                                        <MenuItem value="interview_only">子アカウント</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={user.locked ? "ロック中" : "アクティブ"}
                                                color={user.locked ? "error" : "success"}
                                                size="small"
                                                sx={{ color: '#ffffff' }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            {hasApiKey ? (
                                                <Chip
                                                    icon={<VpnKeyIcon />}
                                                    label="設定済み"
                                                    color="success"
                                                    size="small"
                                                    sx={{ color: '#ffffff' }}
                                                />
                                            ) : (
                                                <Chip
                                                    icon={<KeyIcon />}
                                                    label="未設定"
                                                    color="default"
                                                    size="small"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                <Tooltip title={isAdmin ? "管理者はロックできません" : (user.locked ? "ロック解除" : "ロック")}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleToggleLock(user.id, user.locked)}
                                                            color={user.locked ? "warning" : "default"}
                                                            disabled={isAdmin}
                                                        >
                                                            {user.locked ? <LockIcon /> : <LockOpenIcon />}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title={isAdmin ? "管理者は削除できません" : "削除"}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDeleteUser(user.id)}
                                                            disabled={isAdmin}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title={hasApiKey ? "APIキーを更新" : "APIキーを設定"}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenApiKeyModal(user.id)}
                                                        color="primary"
                                                    >
                                                        <VpnKeyIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                {hasApiKey && (
                                                    <Tooltip title="APIキーを削除">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteApiKey(user.id)}
                                                            color="error"
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="パスワードを変更">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenPasswordModal(user.id)}
                                                        color="primary"
                                                    >
                                                        <LockIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="メールアドレスを変更">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenEmailModal(user.id)}
                                                        color="primary"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* 新規ユーザー作成モーダル */}
                <Dialog open={openCreateModal} onClose={handleCloseCreateModal}>
                    <DialogTitle>新規ユーザー作成</DialogTitle>
                    <DialogContent>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <TextField
                                label="メールアドレス"
                                fullWidth
                                value={newUserEmail}
                                onChange={(e) => setNewUserEmail(e.target.value)}
                            />
                            <TextField
                                label="初期パスワード"
                                type="password"
                                fullWidth
                                value={newUserPassword}
                                onChange={(e) => setNewUserPassword(e.target.value)}
                            />
                            <FormControl fullWidth>
                                <InputLabel>ロール</InputLabel>
                                <Select
                                    value={newUserRole}
                                    label="ロール"
                                    onChange={(e) => setNewUserRole(e.target.value)}
                                >
                                    <MenuItem value="general">一般ユーザー</MenuItem>
                                    <MenuItem value="admin">管理者</MenuItem>
                                    <MenuItem value="custom">カスタム</MenuItem>
                                    <MenuItem value="interview_only">子アカウント</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseCreateModal}>キャンセル</Button>
                        <Button variant="contained" onClick={handleCreateUser}>
                            作成
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* OpenAI API Key更新モーダル */}
                <Dialog open={openApiKeyModal} onClose={handleCloseApiKeyModal}>
                    <DialogTitle>OpenAI API Key設定</DialogTitle>
                    <DialogContent>
                        {apiKeyError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {apiKeyError}
                            </Alert>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            {selectedUserId && userList.find(u => u.id === selectedUserId)?.openai_api_key && (
                                <Alert severity="info" sx={{ mb: 1 }}>
                                    現在のAPIキー: {maskApiKey(userList.find(u => u.id === selectedUserId)?.openai_api_key)}
                                </Alert>
                            )}
                            <TextField
                                label="新しいOpenAI API Key"
                                fullWidth
                                value={newApiKey}
                                onChange={(e) => setNewApiKey(e.target.value)}
                                placeholder="sk-..."
                                helperText="新しいOpenAIのAPIキーを入力してください"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseApiKeyModal}>キャンセル</Button>
                        <Button
                            variant="contained"
                            onClick={handleUpdateApiKey}
                            disabled={!newApiKey.startsWith('sk-')}
                        >
                            保存
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* パスワード変更モーダル */}
                <Dialog open={openPasswordModal} onClose={handleClosePasswordModal}>
                    <DialogTitle>パスワード変更</DialogTitle>
                    <DialogContent>
                        {passwordError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {passwordError}
                            </Alert>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <TextField
                                label="新しいパスワード"
                                type="password"
                                fullWidth
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                helperText="6文字以上で入力してください"
                            />
                            <TextField
                                label="新しいパスワード（確認）"
                                type="password"
                                fullWidth
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClosePasswordModal}>キャンセル</Button>
                        <Button
                            variant="contained"
                            onClick={handleUpdatePassword}
                            disabled={!newPassword || !confirmPassword}
                        >
                            変更
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* メールアドレス変更モーダル */}
                <Dialog open={openEmailModal} onClose={handleCloseEmailModal}>
                    <DialogTitle>メールアドレス変更</DialogTitle>
                    <DialogContent>
                        {emailError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {emailError}
                            </Alert>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <TextField
                                label="メールアドレス"
                                type="email"
                                fullWidth
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                helperText="新しいメールアドレスを入力してください"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseEmailModal}>キャンセル</Button>
                        <Button
                            variant="contained"
                            onClick={handleUpdateEmail}
                            disabled={!newEmail || !newEmail.includes('@')}
                        >
                            変更
                        </Button>
                    </DialogActions>
                </Dialog>
            </AccordionDetails>
        </Accordion>
    );
}